/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 폼 필드 공통 클래스를 정의한다.
 *
 * @file /src/components/form/field/Field.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 9. 25.
 */
import * as parent from '@base/Component';
import { Button } from '@components/button/Button';
import { Dom } from '@utils/Dom';
import { Format } from '@utils/Format';
import { Html } from '@utils/Html';
import { Language, LanguageText } from '@utils/Language';
import { Form } from '@components/form/Form';
import './Field.css';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 필드값이 변경되었을 때
     */
    change?: (field: T, value: any, previousValue: any) => void | Promise<void>;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 필드를 찾거나, 필드값을 지정하거나 필드값을 전송할 때 사용되는 필드명 (미지정시 name 과 동일)
     */
    inputName?: string;

    /**
     * 필드값
     */
    value?: any;

    /**
     * 라벨텍스트
     */
    label?: LanguageText;

    /**
     * 라벨위치
     */
    labelPosition?: 'top' | 'left';

    /**
     * 라벨정렬
     */
    labelAlign?: 'left' | 'center' | 'right';

    /**
     * 라벨길이
     */
    labelLength?: number;

    /**
     * 라벨너비
     */
    labelWidth?: number;

    /**
     * 라벨구분자
     */
    labelSeparator?: string;

    /**
     * 라벨스타일
     */
    labelStyle?: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>>;

    /**
     * 라벨박스형식
     */
    labelBox?: 'default' | 'confirm' | 'danger';

    /**
     * 필드 컨테이너 스타일시트
     */
    fieldClass?: string | string[];

    /**
     * 필드 정렬
     */
    fieldAlign?: string;

    /**
     * 필드길이
     */
    fieldLength?: number;

    /**
     * 필드너비
     */
    fieldWidth?: number;

    /**
     * 도움말
     */
    helpText?: LanguageText;

    /**
     * 값이 존재하지 않을 경우 기본값
     */
    defaultValue?: any;

    /**
     * 값 초기화시 change 이벤트 발생여부
     */
    initOnChange?: boolean;

    /**
     * 공백허용여부
     */
    allowBlank?: boolean;

    /**
     * 읽기전용여부
     */
    readonly?: boolean;

    /**
     * 버튼
     */
    buttons?: (Pick<Button['properties'], 'icon' | 'text' | 'buttonClass'> & { handler(field: Field): void })[];

    /**
     * 필드값 유효성 체크 함수
     */
    validator?: (value: string, field: parent.Component) => Promise<boolean | string>;

    /**
     * 이벤트 리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Field extends parent.Component {
    /**
     * 컴포넌트 타입
     */
    type = 'field';

    /**
     * 컴포넌트 확장타입
     */
    abstract variant: string;

    /**
     * 필드를 찾거나, 필드값을 지정하거나 필드값을 전송할 때 사용되는 필드명 (미지정시 name 과 동일)
     */
    inputName: string;

    /**
     * 필드값
     */
    value: any = null;

    /**
     * 필드 RAW 값 (필드값이 확정되기 전)
     */
    rawValue: any = null;

    /**
     * 필드 초기값
     */
    initValue: any = null;

    /**
     * 필드 이전값
     */
    private previousValue: any = null;

    /**
     * setValue 직렬 처리 큐
     */
    #valueQueue: Promise<void> = Promise.resolve();

    /**
     * 현재 활성 slot 의 resolve 함수
     */
    #resolveCurrentSlot: (() => void) | null = null;

    /**
     * 라벨텍스트
     */
    label: LanguageText | null;

    /**
     * 라벨위치
     */
    labelPosition: 'top' | 'left';

    /**
     * 라벨정렬
     */
    labelAlign: 'left' | 'center' | 'right';

    /**
     * 라벨길이
     */
    labelLength: number | null;

    /**
     * 라벨너비
     */
    labelWidth: number | null;

    /**
     * 라벨구분자
     */
    labelSeparator: string | null;

    /**
     * 라벨스타일
     */
    labelStyle: Partial<Omit<CSSStyleDeclaration, 'width' | 'height' | 'padding' | 'margin'>> | null;

    /**
     * 라벨박스형식
     */
    labelBox: 'default' | 'confirm' | 'danger';

    /**
     * 필드 컨테이너 스타일시트
     */
    fieldClass: string | string[] | null;

    /**
     * 필드 정렬
     */
    fieldAlign: string;

    /**
     * 필드길이
     */
    fieldLength: number | null;

    /**
     * 필드너비
     */
    fieldWidth: number | null;

    /**
     * 도움말
     */
    helpText: LanguageText | null;

    /**
     * 값이 존재하지 않을 경우 기본값
     */
    defaultValue: any;

    /**
     * 값 초기화시 change 이벤트 발생여부
     */
    initOnChange: boolean = true;

    /**
     * 공백허용여부
     */
    allowBlank: boolean;

    /**
     * 읽기전용여부
     */
    readonly: boolean;

    /**
     * 버튼
     */
    buttons: (Pick<Button['properties'], 'icon' | 'text' | 'buttonClass'> & { handler(field: Field): void })[] | null =
        null;

    /**
     * 필드값 유효성 체크 함수
     */
    validator: (value: string, field: parent.Component) => Promise<boolean | string>;

    /**
     * 필드값 유효성 체크 결과
     */
    validation: { valid: boolean; message?: string } = { valid: true };

    /**
     * 필드값 유효성 체크 히스토리
     */
    validationResults: Map<string, { valid: boolean; message?: string }> = new Map();

    /**
     * 자식필드 유효성 체크
     */
    validations: Map<Field, { valid: boolean; message?: string }> = new Map();

    /**
     * 라벨 Dom
     */
    $label: Dom = null;

    /**
     * helpText DOM
     */
    $helpText: Dom = null;

    /**
     * 필드값이 한번이라도 초기화되었는지 여부
     */
    isInit: boolean = false;

    /**
     * 필드 공용 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(public properties: Omit<Properties<Field>, 'height' | 'minHeight' | 'maxHeight'> = null) {
        super(properties);

        this.inputName = this.properties?.inputName !== undefined ? this.properties.inputName : this.name;
        this.initValue = this.properties?.value ?? null;

        this.height = null;
        this.minHeight = null;
        this.maxHeight = null;

        this.allowBlank = this.properties?.allowBlank !== false;

        this.label = this.properties?.label ?? null;
        this.labelPosition = this.properties?.labelPosition ?? null;
        this.labelAlign = this.properties?.labelAlign ?? null;
        this.labelLength = this.properties?.labelLength ?? null;
        this.labelWidth = this.properties?.labelWidth ?? null;
        this.labelSeparator = this.properties?.labelSeparator ?? null;
        this.labelStyle = this.properties?.labelStyle ?? null;
        this.labelBox = this.properties?.labelBox ?? null;

        this.fieldClass = this.properties?.fieldClass ?? null;
        this.fieldAlign = this.properties?.fieldAlign ?? 'left';
        this.fieldLength = this.properties?.fieldLength ?? null;
        this.fieldWidth = this.properties?.fieldWidth ?? null;

        this.helpText = this.properties?.helpText ?? null;

        this.initOnChange = this.properties?.initOnChange !== false;
        this.readonly = this.properties?.readonly === true;
        this.buttons = this.properties?.buttons ?? null;
        this.defaultValue = this.properties?.defaultValue ?? null;
        this.validator = this.properties?.validator ?? null;
    }

    /**
     * 자식 컴포넌트를 초기화한다.
     */
    initItems(): void {
        super.initItems();
    }

    /**
     * 라벨 DOM 을 가져온다.
     *
     * @returns 라벨 DOM
     */
    $getLabel(): Dom {
        if (this.$label === null) {
            this.$label = Html.create('label');
            if (this.labelStyle !== null) {
                Object.assign(this.$label.getEl().style, this.labelStyle);
            }
        }

        return this.$label;
    }

    /**
     * helpText DOM 을 가져온다.
     *
     * @returns helpText DOM
     */
    $getHelpText(): Dom {
        if (this.$helpText === null) {
            this.$helpText = Html.create('aside');
        }

        return this.$helpText;
    }

    /**
     * 현재 필드내에 특정 필드명의 자식필드가 있다면 해당 필드를 가져온다.
     *
     * @param name - 필드명
     * @returns 필드
     */
    getField<T extends Field = Field>(name: string): T | null {
        for (const field of this.getFields(true)) {
            if (field.inputName === name) {
                return field as T;
            }
        }

        return null;
    }

    /**
     * 현재 필드를 포함하여, 자식 필드가 있다면 모두 가져온다.
     *
     * @param is_children - 현재필드의 inputName 과 무관하게 자식필드만 가져올지 여부
     * @returns 전체필드
     */
    getFields(is_children: boolean = false): Field[] {
        const fields: Field[] = [];
        if (is_children == false && this.inputName !== null) {
            fields.push(this);
        } else {
            for (const item of this.items ?? []) {
                if (item instanceof Field) {
                    fields.push(...item.getFields());
                }
            }
        }

        return fields;
    }

    /**
     * 필드값을 지정한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    async setValue(value: any, is_init: boolean = false): Promise<void> {
        await this.lockValue();
        await this.releaseValue(value, is_init);
    }

    /**
     * 필드값을 가져온다.
     *
     * @returns 필드값
     */
    async getValue(): Promise<any> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        return this.value ?? this.defaultValue;
    }

    /**
     * 모든 필드값을 가져온다.
     *
     * @returns 모든 필드값
     */
    async getValues(): Promise<{ [key: string]: any }> {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        const values: { [key: string]: any } = {};
        if (this.isDisabled() == true) {
            return values;
        }

        if (this.inputName === null) {
            for (const field of this.getFields()) {
                if (field instanceof Field) {
                    Object.assign(values, await field.getValues());
                }
            }
        } else {
            values[this.inputName] = await this.getValue();
        }

        return values;
    }

    /**
     * 필드가 속한 폼 패널을 가져온다.
     *
     * @returns 폼 패널
     */
    getForm(): Form | null {
        let parent = this.getParent();
        while (parent !== null) {
            if (parent.type == 'panel' && parent.variant == 'form') {
                return parent as Form;
            }

            parent = parent.getParent();
        }

        return null;
    }

    /**
     * 필드값이 비어있는지 확인한다.
     *
     * @returns 빈값여부
     */
    async isBlank(value: any = undefined): Promise<boolean> {
        if (value === undefined) {
            value = await this.getValue();
        }

        if (value === null) {
            return true;
        } else if (typeof value == 'object') {
            if (value.length == 0) {
                return true;
            }
        } else if (value.toString().length == 0) {
            return true;
        }

        return false;
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @returns 유효성여부
     */
    async isValid(value: any = undefined): Promise<boolean> {
        if (this.isDisabled() === true) {
            return true;
        }

        if (value === undefined) {
            value = await this.getValue();
        }

        const validation = await this.validate(value);

        this.validationResults.set(JSON.stringify(value), validation);
        this.updateValidation(validation);

        return this.validation.valid;
    }

    /**
     * 필드값이 유효한지 확인한다.
     *
     * @param value - 유효성결과를 검증할 값 (정의되지 않은 경우, 현재 값)
     * @returns 필드값 유효성결과
     */
    protected async validate(value: any = undefined): Promise<{ valid: boolean; message?: string }> {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        if (value === undefined) {
            value = await this.getValue();
        }

        let validation = this.getValidationHistory(value);
        if (validation === null) {
            validation = { valid: true };

            const isBlank = await this.isBlank(value);
            if (this.allowBlank === false && isBlank == true) {
                validation.valid = false;
                validation.message = await Language.getError('REQUIRED').getText();
            } else if (typeof this.validator == 'function') {
                const results = await this.validator(value, this);
                if (typeof results == 'boolean') {
                    validation.valid = results;
                } else {
                    validation.valid = false;
                    validation.message = results;
                }
            }
        }

        return validation;
    }

    /**
     * 필드값 검증결과를 가져온다.
     *
     * @returns 유효성 검증결과
     */
    getValidation(): { valid: boolean; message?: string } {
        if (this.isDisabled() === true) {
            return { valid: true };
        }

        return this.validation;
    }

    /**
     * 유효성 검증기록이 존재하는지 확인하고, 있다면 그 값을 가져온다.
     *
     * @param value - 유효성검증을 할 값
     * @param is_empty_true - 검증기록이 없을 경우 유효한것으로 판단할지 여부
     * @returns 유효성 검증기록
     */
    getValidationHistory(value: any, is_empty_true: boolean = false): { valid: boolean; message?: string } | null {
        const validation = this.validationResults.get(JSON.stringify(value));
        return validation ?? (is_empty_true == true ? { valid: true } : null);
    }

    /**
     * 유효성 검증결과를 업데이트한다.
     *
     * @param validation - 검증결과 또는 자식필드로부터 전파되었을경우, 자식필드
     */
    updateValidation(validation: { valid: boolean; message?: string } | Field): void {
        if (validation instanceof Field) {
            this.validations.set(validation, validation.getValidation());

            let valid: boolean = true;
            this.validations.forEach((validation) => {
                valid = valid === true && validation.valid === true;
            });

            this.validation = { valid: valid };
        } else {
            this.validation = validation;
        }
        this.setError(this.validation.valid == false);

        /**
         * 부모 컴포넌트가 필드이고, 해당 필드가 에러메시지를 통합해서 보일 수 있는 경우, 부모 필드에게 유효성 결과를 전파한다.
         */
        const parentField = this.getParent();
        if (parentField instanceof Field && parentField.isCombineValidate() == true) {
            this.getParent<Field>().updateValidation(this);
            return;
        }

        this.setErrorMessage(this.validation.message ?? null);
    }

    /**
     * 필드 유효성 검증결과 통합여부를 가져온다.
     *
     * @returns 필드 유효성 검증결과 통합여부
     */
    isCombineValidate(): boolean {
        return false;
    }

    /**
     * 필드값 검증결과를 가져온다.
     *
     * @returns 유효성 확인결과
     */
    isValidated(): boolean {
        if (this.isDisabled() === true) {
            return true;
        }

        return this.validation.valid;
    }

    /**
     * 변경된 필드값을 롤백한다.
     *
     * @param is_fire_event - 변경이벤트 발생여부
     */
    async rollback(is_fire_event: boolean = true): Promise<void> {
        if (this.isUpdated() == true) {
            if (this.initValue !== undefined) {
                this.setValue(this.initValue, true);
            } else {
                this.setValue(null, true);
            }

            if (is_fire_event == true) {
                this.onChange();
            }
        }
    }

    /**
     * 필드값 변경여부를 가져온다.
     *
     * @return 필드값 변경여부
     */
    isUpdated(): boolean {
        return Format.isEqual(this.value, this.initValue) !== true;
    }

    /**
     * 라벨을 변경한다.
     *
     * @param label - 라벨텍스트
     */
    async setLabel(label: LanguageText): Promise<void> {
        this.label = label;

        if (label === null) {
            this.$getLabel().empty();
        } else {
            this.$getLabel().html(await Language.resolveText(label));
        }
    }

    /**
     * 도움말을 변경한다.
     *
     * @param text - 도움말
     * @param is_replace - 기존 도움말을 업데이트할지 여부
     */
    async setHelpText(text: LanguageText, is_replace: boolean = true): Promise<void> {
        if (is_replace == true) {
            this.helpText = text;
        }

        if (text === null) {
            this.$getContainer().addClass('slient');
            this.$getHelpText().empty();
            return;
        } else {
            if (this.$getContainer().hasClass('error') == false) {
                this.$getContainer().removeClass('slient');
            }
        }

        this.$getHelpText().html(await Language.resolveText(text));
    }

    /**
     * 에러메시지를 변경한다.
     *
     * @param is_error - 에러여부
     */
    setError(is_error: boolean): void {
        if (is_error === true) {
            this.$getContainer().addClass('error');
        } else {
            this.$getContainer().removeClass('error');
        }
    }

    /**
     * 에러메시지를 표시한다.
     *
     * @param message - 에러메시지
     */
    setErrorMessage(message: string = null): void {
        if (this.isCombineValidate() == true) {
            const messages: string[] = [];
            if (message !== null) {
                messages.push(message);
            }
            this.validations.forEach((validation) => {
                if (validation.valid === false) {
                    const message = validation.message ?? null;
                    if (message !== null && messages.includes(message) == false) {
                        messages.push(message);
                    }
                }
            });

            message = messages.length > 0 ? messages.join('<br>') : null;
        }

        this.setHelpText(message ?? this.helpText, false);
    }

    /**
     * 읽기전용상태를 변경한다.
     *
     * @param readonly - 읽기전용여부
     */
    setReadonly(readonly: boolean): void {
        this.readonly = readonly;
        this.setButtonsDisabled(readonly === true || this.disabled === true);
    }

    /**
     * 컴포넌트의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     * @returns 컴포넌트
     */
    setDisabled(disabled: boolean): this {
        this.setButtonsDisabled(disabled === true || this.readonly === true);
        return super.setDisabled(disabled);
    }

    /**
     * 추가 버튼의 비활성화여부를 설정한다.
     *
     * @param disabled - 비활성화여부
     */
    setButtonsDisabled(disabled: boolean): void {
        const $buttons = Html.get('div[data-role=buttons]', this.$getContainer());
        if ($buttons === null) {
            return;
        }

        Html.all('button', $buttons).forEach(($button) => {
            $button.setDisabled(disabled);
        });
    }

    /**
     * 공백허용여부를 변경한다.
     *
     * @param allowBlank - 곰백허용여부
     */
    setAllowBlank(allowBlank: boolean): void {
        this.allowBlank = allowBlank !== false;
        this.$getContainer().toggleClass('required', this.allowBlank === false);
        this.validationResults.clear();
    }

    /**
     * 컴포넌트 DOM 을 렌더링한다.
     */
    async renderDom(): Promise<void> {
        await super.renderDom();

        this.$getComponent().setStyleProperty('--aui-field-anchor-name', `--Aui-field-${this.id}`);

        if (this.labelAlign !== null) {
            this.$getComponent().setStyleProperty('--aui-field-label-align', this.labelAlign);
            this.$getComponent().setStyleProperty('--aui-field-label-padding', '0');
        }

        if (this.labelWidth !== null) {
            this.$getComponent().setStyleProperty('--aui-field-label-width', `${this.labelWidth}px`);
        } else if (this.labelLength !== null) {
            this.$getComponent().setStyleProperty('--aui-field-label-width', `${this.labelLength}rem`);
        }

        if (this.labelPosition !== null) {
            if (this.labelPosition == 'left') {
                this.$getComponent().setStyleProperty('--aui-field-columns', 'auto 1fr auto');
                this.$getComponent().setStyleProperty(
                    '--aui-field-areas',
                    `'label content buttons' 'label helptext helptext'`
                );
                this.$getComponent().setStyleProperty('--aui-field-label-padding', '0 var(--aui-component-gap) 0 0');
            } else {
                this.$getComponent().setStyleProperty('--aui-field-label-align', this.labelAlign ?? 'left');
                this.$getComponent().setStyleProperty('--aui-field-label-width', '100%');
                this.$getComponent().setStyleProperty('--aui-field-columns', '1fr auto');
                this.$getComponent().setStyleProperty(
                    '--aui-field-areas',
                    `'label label' 'content buttons' 'helptext helptext'`
                );
            }
        }

        if (this.labelSeparator !== null) {
            this.$getComponent().setStyleProperty('--aui-field-label-separator', `"${this.labelSeparator}"`);
        }
    }

    /**
     * 추가 버튼을 렌더링한다.
     */
    async renderButtons(): Promise<void> {
        const $buttons = Html.create('div', { 'data-role': 'buttons' });

        this.buttons.forEach(async (button) => {
            const $button = Html.create('button', {
                type: 'button',
            });
            $button.setDisabled(this.disabled === true || this.readonly === true);

            button.text ??= null;
            button.icon ??= null;
            button.buttonClass ??= null;
            button.handler ??= null;

            if (button.buttonClass !== null) {
                $button.addClass(...button.buttonClass.split(' '));
            }

            if (button.icon !== null) {
                const $icon = Html.icon(button.icon);
                $button.append($icon);
            }

            if (button.text !== null) {
                const $text = Html.create('span');
                $text.html(button.text);
                $button.append($text);
            }

            if (button.handler !== null) {
                $button.on('click', () => {
                    button.handler(this);
                });
            }

            $buttons.append($button);
        });

        this.$getContainer().append($buttons);
    }

    /**
     * 필드영역을 렌더링한다.
     */
    async renderField(): Promise<void> {
        this.$getContent().empty();
    }

    /**
     * 컴포넌트 컨테이너를 렌더링한다.
     */
    async renderContainer(): Promise<void> {
        const $label = this.$getLabel();
        if (this.fieldClass !== null) {
            this.$getContainer().addClass(...(Array.isArray(this.fieldClass) ? this.fieldClass : [this.fieldClass]));
        }

        if (this.label !== null) {
            await this.setLabel(this.label);
        }
        this.$getContainer().append($label);

        if (this.labelBox !== null) {
            this.$getContainer().setAttr('data-label-box', this.labelBox);
        }

        if (this.allowBlank === false) {
            this.$getContainer().addClass('required');
        }

        if (this.fieldWidth !== null) {
            this.$getContent().setStyle('width', this.fieldWidth + 'px');
        } else if (this.fieldLength !== null) {
            this.$getContent().setStyle('width', this.fieldLength + 'rem');
        } else {
            if (this.labelPosition == 'left') {
                this.$getContainer().setStyle('grid-template-columns', 'auto 1fr');
            }
        }
        this.$getContainer().append(this.$getContent());
        this.$getContent().setStyle('anchor-name', `--Aui-field-${this.id}`);

        await this.renderField();

        const $focusable = Html.get('*[id$="-focusable"]', this.$getContent());
        if ($focusable !== null) {
            $label.setAttr('for', $focusable.getAttr('id'));
        }

        if (this.buttons !== null) {
            await this.renderButtons();
        }

        const $helpText = this.$getHelpText();
        await this.setHelpText(this.helpText);
        this.$getContainer().append($helpText);
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        if (this.initValue !== null) {
            await this.setValue(this.initValue, true);
        }

        /**
         * 부모가 폼 패널이 아닌 경우, 도움말을 툴팁으로 띄워서 보여준다.
         */
        if (this.getForm() === null) {
            this.$getContainer().addClass('tooltip');
        }

        await super.renderState();
    }

    /**
     * 필드값을 초기화한다.
     *
     * @param is_fire_event - 이벤트발생여부
     * @param value - 초기화할 값 (undefined 인 경우 초기값)
     */
    async reset(is_fire_event: boolean = true, value: any): Promise<void> {
        if (value === undefined) {
            value = this.initValue ?? null;
        }

        if (is_fire_event === true) {
            await this.releaseValue(value, false);
        } else {
            this.value = value;
        }
    }

    /**
     * 컴포넌트를 제거한다.
     *
     * @param removeDom - 컴포넌트 DOM 을 제거할지 여부 (기본값 : true)
     */
    async remove(removeDom: boolean = true): Promise<void> {
        await this.waitForEvents();

        this.$label = null;
        this.$helpText = null;
        this.value = null;
        this.initValue = null;
        this.rawValue = null;
        this.validationResults.clear();
        this.validations.clear();
        this.validator = null;

        await super.remove(removeDom);
    }

    /**
     * 입력값이 변경되었을 때 이벤트를 처리한다.
     */
    async onChange(): Promise<void> {
        await this.fireEvent('change', [this, await this.getValue(), this.previousValue]);
    }

    /**
     * 필드값을 지정하기전, 큐를 등록한다.
     */
    readonly lockValue = async (): Promise<void> => {
        if (this.isRendered() == false) {
            await this.render(true);
        }

        let resolve: () => void;
        const mySlot = new Promise<void>((res) => {
            resolve = res;
        });

        const previous = this.#valueQueue;
        this.#valueQueue = mySlot;

        await previous;

        this.#resolveCurrentSlot = resolve;
    };

    /**
     * 필드값을 실질적으로 변경하고, 큐 등록을 해제한다.
     *
     * @param value - 값
     * @param is_init - 초기값여부
     */
    readonly releaseValue = async (value: any = undefined, is_init: boolean = false): Promise<void> => {
        if (value === undefined) {
            this.#resolveCurrentSlot?.();
            this.#resolveCurrentSlot = null;
            return;
        }

        if (is_init == true) {
            this.isInit = true;
            this.initValue = value;
        }

        this.value = value;

        const oValue = await this.getValue();
        const pValue = JSON.parse(JSON.stringify(this.previousValue));

        if (Format.isEqual(pValue, oValue) == false) {
            this.previousValue = JSON.parse(JSON.stringify(oValue));
        }

        this.#resolveCurrentSlot?.();
        this.#resolveCurrentSlot = null;

        if (is_init == false || this.initOnChange == true) {
            if (Format.isEqual(pValue, oValue) == false) {
                await this.onChange();
            }
        }
    };

    /**
     * 필드값이 지정되고 있는지 확인한다.
     *
     * @returns 필드값이 지정되고 있는지 여부
     */
    readonly isLoading = (): boolean => {
        if (this.#resolveCurrentSlot !== null) {
            return true;
        }

        for (const field of this.getFields(true)) {
            if (field.isLoading() == true) {
                return true;
            }
        }

        return false;
    };
}
